import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  useContacts,
  useContactStats,
  useMarkContactAsRead,
  useDeleteContact,
} from "@/lib/hooks";
import { Contact } from "@/lib/types";
import { useLanguage } from "@/lib/language";
import {
  MessageSquare,
  Eye,
  Trash2,
  Phone,
  Mail,
  Calendar,
  MapPin,
  Filter,
  Search,
} from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";

interface ContactListProps {
  onContactSelect?: (contact: Contact) => void;
}

export function ContactList({ onContactSelect }: ContactListProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [readFilter, setReadFilter] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [showContactDialog, setShowContactDialog] = useState(false);
  const { t, dir } = useLanguage();

  const isRead = readFilter === "all" ? undefined : readFilter === "read";
  const {
    data: contactsData,
    isLoading,
    error,
  } = useContacts(currentPage, isRead);
  const { data: stats } = useContactStats();
  const markAsReadMutation = useMarkContactAsRead();
  const deleteContactMutation = useDeleteContact();

  const handleMarkAsRead = async (contactId: number) => {
    try {
      await markAsReadMutation.mutateAsync(contactId);
      toast.success("Contact marked as read");
    } catch (error) {
      toast.error("Failed to mark contact as read");
    }
  };

  const handleDeleteContact = async (contactId: number) => {
    try {
      await deleteContactMutation.mutateAsync(contactId);
      toast.success("Contact deleted successfully");
    } catch (error) {
      toast.error("Failed to delete contact");
    }
  };

  const handleViewContact = (contact: Contact) => {
    setSelectedContact(contact);
    setShowContactDialog(true);
    if (!contact.is_read) {
      handleMarkAsRead(contact.id);
    }
  };

  const filteredContacts =
    contactsData?.contacts.filter(
      (contact) =>
        contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contact.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contact.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (contact.property_title &&
          contact.property_title
            .toLowerCase()
            .includes(searchTerm.toLowerCase()))
    ) || [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        <p className="mt-2 text-muted-foreground">{t("loadingContacts")}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">{t("errorLoadingContacts")}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                {t("totalContacts")}
              </CardTitle>
              <MessageSquare className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total_contacts}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                {t("unreadMessages")}
              </CardTitle>
              <Badge variant="destructive" className="h-6 w-6 p-0">
                {stats.unread_contacts}
              </Badge>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.unread_contacts}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                {t("recentContacts")}
              </CardTitle>
              <Calendar className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.recent_contacts}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            {t("filtersAndSearch")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder={t("searchContacts")}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={readFilter} onValueChange={setReadFilter} dir={dir}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder={t("filterByStatus")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("allMessages")}</SelectItem>
                <SelectItem value="unread">{t("unreadOnly")}</SelectItem>
                <SelectItem value="read">{t("readOnly")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Contact List */}
      <Card>
        <CardHeader>
          <CardTitle>{t("contactMessages")}</CardTitle>
        </CardHeader>
        <CardContent>
          {filteredContacts.length === 0 ? (
            <div className="text-center py-8">
              <MessageSquare className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                {t("noContactsFound")}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchTerm
                  ? t("tryAdjustingSearchTerms")
                  : t("noContactMessages")}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredContacts.map((contact) => (
                <div
                  key={contact.id}
                  className={`p-4 border rounded-lg cursor-pointer transition-colors hover:bg-gray-50 ${
                    !contact.is_read ? "bg-blue-50 border-blue-200" : "bg-white"
                  }`}
                  onClick={() => handleViewContact(contact)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-medium text-gray-900">
                          {contact.name}
                        </h3>
                        {!contact.is_read && (
                          <Badge variant="default">New</Badge>
                        )}
                        <Badge variant="outline">{contact.language}</Badge>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-600 mb-2">
                        <div className="flex items-center gap-1 min-w-0">
                          <Mail className="h-3 w-3 flex-shrink-0" />
                          <span className="truncate">{contact.email}</span>
                        </div>
                        {contact.phone && (
                          <div className="flex items-center gap-1">
                            <Phone className="h-3 w-3 flex-shrink-0" />
                            {contact.phone}
                          </div>
                        )}
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3 flex-shrink-0" />
                          {format(
                            new Date(contact.created_at),
                            "MMM d, yyyy HH:mm"
                          )}
                        </div>
                      </div>
                      {contact.property_title && (
                        <div className="flex items-center gap-1 text-sm text-gray-600 mb-2">
                          <MapPin className="h-3 w-3" />
                          <span className="font-medium">Property:</span>{" "}
                          {contact.property_title}
                        </div>
                      )}
                      <p className="text-gray-700 line-clamp-2">
                        {contact.message}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewContact(contact);
                        }}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Contact</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete this contact
                              message? This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDeleteContact(contact.id)}
                              className="bg-red-600 hover:bg-red-700"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {contactsData && contactsData.pagination.pages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <div className="text-sm text-gray-700">
                Showing{" "}
                {(contactsData.pagination.page - 1) *
                  contactsData.pagination.per_page +
                  1}{" "}
                to{" "}
                {Math.min(
                  contactsData.pagination.page *
                    contactsData.pagination.per_page,
                  contactsData.pagination.total
                )}{" "}
                of {contactsData.pagination.total} results
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!contactsData.pagination.has_prev}
                  onClick={() => setCurrentPage(currentPage - 1)}
                >
                  Previous
                </Button>
                <span className="text-sm">
                  Page {contactsData.pagination.page} of{" "}
                  {contactsData.pagination.pages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!contactsData.pagination.has_next}
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Contact Details Dialog */}
      <Dialog open={showContactDialog} onOpenChange={setShowContactDialog}>
        <DialogContent className="max-w-2xl w-[95vw] sm:w-full">
          <DialogHeader>
            <DialogTitle>Contact Details</DialogTitle>
          </DialogHeader>
          {selectedContact && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Name
                  </label>
                  <p className="mt-1">{selectedContact.name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Email
                  </label>
                  <p className="mt-1">{selectedContact.email}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Phone
                  </label>
                  <p className="mt-1">
                    {selectedContact.phone || "Not provided"}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Language
                  </label>
                  <p className="mt-1">{selectedContact.language}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Date
                  </label>
                  <p className="mt-1">
                    {format(
                      new Date(selectedContact.created_at),
                      "MMMM d, yyyy 'at' HH:mm"
                    )}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Status
                  </label>
                  <p className="mt-1">
                    <Badge
                      variant={
                        selectedContact.is_read ? "secondary" : "default"
                      }
                    >
                      {selectedContact.is_read ? "Read" : "Unread"}
                    </Badge>
                  </p>
                </div>
              </div>

              {selectedContact.property_title && (
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Property
                  </label>
                  <p className="mt-1">{selectedContact.property_title}</p>
                </div>
              )}

              <div>
                <label className="text-sm font-medium text-gray-500">
                  Message
                </label>
                <div className="mt-1 p-3 bg-gray-50 rounded-md border">
                  <p className="whitespace-pre-wrap">
                    {selectedContact.message}
                  </p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
